import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toSlug } from "common";
import { useUser } from "../store";
import { useRemoteSearch } from "../hooks/useRemoteSearch";
import "./GlobalSearch.scss";

type ResidentResult = {
  facility: string;
  payer: string;
  residentName: string;
  billType: string;
  sk: string;
};
type ResidentSearchResponse = { results: ResidentResult[]; total: number };

/** The payer's dashboard, without a particular resident */
export const payerPath = (group: string, { facility, payer }: Pick<ResidentResult, "facility" | "payer">) =>
  `/${toSlug(group)}/${toSlug(facility)}/${toSlug(payer)}`;

/** The dashboard of the payer the resident is on, opening that resident's messages */
export const residentPath = (group: string, { facility, payer, sk }: Pick<ResidentResult, "facility" | "payer" | "sk">) =>
  `/${toSlug(group)}/${toSlug(facility)}/${toSlug(payer)}?report=${encodeURIComponent(sk)}`;

/** Finds a resident in any aging report the user can see */
export default function GlobalSearch({ onNavigate }: { onNavigate?: () => void }) {
  const { invokeApi, user } = useUser();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const { status, data, query: searched, tooShort } = useRemoteSearch<ResidentSearchResponse>({
    invokeApi,
    endpoint: "/searchResidents",
    query,
  });

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const results = data?.results ?? [];
  const finish = () => {
    setQuery("");
    setOpen(false);
    onNavigate?.();
  };

  const body = () => {
    if (tooShort) return <p className="global-search-hint">Type at least 2 letters of a resident&apos;s name.</p>;
    if (status === "error") return <p className="global-search-hint is-error">Search is unavailable right now. Try again in a moment.</p>;
    if (status === "loading" && !results.length) return <p className="global-search-hint" role="status">Searching…</p>;
    if (!results.length) return <p className="global-search-hint" role="status">No residents match “{searched}”.</p>;
    return (
      <>
        <ul className="global-search-results" aria-busy={status === "loading"}>
          {results.map((resident) => (
            <li key={`${resident.facility}/${resident.payer}/${resident.sk}`}>
              <Link to={residentPath(user.group, resident)} onClick={finish}>
                <span className="global-search-name">{resident.residentName}</span>
                <span className="global-search-detail">
                  {resident.billType} · {resident.facility} / {resident.payer.toUpperCase()}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        {data && data.total > results.length && (
          <p className="global-search-hint">
            Showing {results.length} of {data.total}. Add more of the name to narrow it down.
          </p>
        )}
      </>
    );
  };

  return (
    <div className="global-search" ref={container} onClick={(event) => event.stopPropagation()}>
      <span className="global-search-icon" aria-hidden="true">
        <i className="fa-solid fa-magnifying-glass" />
      </span>
      <input
        type="search"
        className="global-search-input"
        placeholder="Search residents…"
        aria-label="Search residents in all reports"
        autoComplete="off"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
          if (event.key === "Enter" && results[0]) {
            navigate(residentPath(user.group, results[0]));
            finish();
          }
        }}
      />
      {open && query.trim() && (
        <div className="global-search-panel" role="region" aria-label="Resident search results">
          {body()}
        </div>
      )}
    </div>
  );
}
